import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
} from "@mui/material";
import { CsvImportDialogStyle as styles } from "./CsvImportDialogStyle";
import { useRecoilValue } from "recoil";
import { userSearchOptionsSelector } from "../recoil/selectors/userSearchOptionsSelector";
import { baseSearchOptionsSelector } from "../recoil/selectors/baseSearchOptionsSelector";
import BaseFilterSelect from "./BaseFilter";
import UserFilterSelect from "./UserFilter";
import { loginUserSelector } from "../recoil/selectors/loginUserSelector";
import { canManageUsers } from "../constants/roles";

type Props = {
  open: boolean;
  onClose: () => void;
  onFileDrop: (file: File) => void;
  selectedBaseId: number | null;
  setSelectedBaseId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedUserId: number | null;
  setSelectedUserId: React.Dispatch<React.SetStateAction<number | null>>;
};

const CsvImportDialog: React.FC<Props> = ({
  open,
  onClose,
  onFileDrop,
  selectedBaseId,
  setSelectedBaseId,
  selectedUserId,
  setSelectedUserId,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const userSearchOptions = useRecoilValue(userSearchOptionsSelector);
  const baseSearchOptions = useRecoilValue(baseSearchOptionsSelector);
  const loginUser = useRecoilValue(loginUserSelector);

  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);

  const userOptions = useMemo(() => {
    return (
      userSearchOptions?.map((user) => ({
        label: `${user.userNumber ?? ""}_${user.lastName ?? ""}${
          user.firstName ?? ""
        }`,
        id: user.id!,
      })) ?? []
    );
  }, [userSearchOptions]);

  const baseOptions = useMemo(() => {
    return (
      baseSearchOptions?.map((base) => ({
        label: `${base.name}`,
        id: base.id!,
      })) ?? []
    );
  }, [baseSearchOptions]);

  const handleUserChange = (userId: number | null) => {
    setSelectedUserId(userId);
  };

  const handleBaseChange = (baseId: number | null) => {
    setSelectedBaseId(baseId);
  };

  // CSVフォーマットのダウンロード
  // 静的ファイル(/csvformat.csv)へのリンクだと、Amplifyのデフォルト
  // SPAリライトルール(.csv非対応)によりindex.html(HTML)が返ってしまうため、
  // クライアント側でBlobを生成してダウンロードする。
  const handleDownloadCsvFormat = useCallback(() => {
    const header =
      "出品者SKU,FNSKU,ASIN,商品名,JAN,セット数,予定出荷単位数,備考";
    // 文字コード：UTF-8(BOM付き) ／ 改行コード：CRLF
    const bom = String.fromCharCode(0xfeff);
    const csv = bom + header + "\r\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "csvformat.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        onFileDrop(files[0]);
      }
    },
    [onFileDrop]
  );

  const handleFileSelectClick = () => {
    // if (!isFileDialogOpen) {
    setIsFileDialogOpen(true);
    fileInputRef.current?.click();
    // }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileDrop(file);
    }
    // 同じファイルを選んだときも反応するように値をリセット
    event.target.value = "";
    setIsFileDialogOpen(false);
  };

  // 一般ユーザーの拠点・ユーザーの自動設定は useProductRegistration 側で行う

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      disableEnforceFocus
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        sx: {
          display: "flex",
          width: "calc(100vw - 40px)",
          height: "calc(100vh - 40px)",
          maxWidth: "none",
          maxHeight: "none",
          overflowY: "auto",
        },
      }}
      sx={{ zIndex: 3000 }}
    >
      <DialogTitle>CSVファイルをインポート</DialogTitle>
      <DialogContent>
        <Button onClick={onClose} sx={styles.closeButton}>
          ×
        </Button>
        <Box sx={{ display: "flex", width: "100%", height: "100%" }}>
          {/* 左カラム */}
          <Box sx={styles.leftColumn}>
            <BaseFilterSelect
              options={baseOptions}
              value={selectedBaseId}
              onSelect={handleBaseChange}
              sx={{ width: "50%", marginBottom: "16px" }}
              labelFontSize="0.9rem"
            />
            {canManageUsers(loginUser?.permissionId) && (
              <>
                <UserFilterSelect
                  options={userOptions}
                  value={selectedUserId}
                  onSelect={handleUserChange}
                  sx={{ width: "50%", marginBottom: "16px" }}
                  labelFontSize="0.9rem"
                />
              </>
            )}
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <Button
              sx={styles.orangeButton}
              onClick={handleFileSelectClick}
              disabled={!selectedBaseId}
            >
              ファイル読込
            </Button>
            <Box
              onDragOver={(e) => {
                if (selectedBaseId) {
                  handleDragOver(e);
                } else {
                  e.preventDefault();
                }
              }}
              onDragLeave={selectedBaseId ? handleDragLeave : undefined}
              onDrop={(e) => {
                if (selectedBaseId) {
                  handleDrop(e);
                } else {
                  e.preventDefault();
                }
              }}
              sx={{
                ...styles.dropArea,
                ...(dragOver && selectedBaseId ? styles.dropAreaHover : {}),
                backgroundColor: selectedBaseId
                  ? styles.dropArea.backgroundColor
                  : "#ccc",
                cursor: selectedBaseId ? "pointer" : "not-allowed",
              }}
            >
              <Typography>
                {selectedBaseId
                  ? "ここにファイルをドロップしてください"
                  : "拠点を選択した後にファイルをドロップしてください"}
              </Typography>
            </Box>{" "}
          </Box>

          {/* 右カラム */}
          <Box sx={styles.rightColumn}>
            <Typography>
              ・手順
              <br />1{" "}
              <Link
                component="button"
                type="button"
                onClick={handleDownloadCsvFormat}
                color="primary"
                underline="hover"
                sx={styles.link}
              >
                こちら
              </Link>{" "}
              からcsvフォーマットをダウンロード
              <br />
              2 ダウンロードしたファイルにデータを入力
              <br />
              3 入力後にCSVファイルの文字コード および 改行コードを確認
              <br />
              　※文字コード：UTF-8 ／ 改行コード：CRLF
              <br />
              4
              指定のファイルを「ドラッグ＆ドロップ」または「ファイル読込み」から選択
              <br />
              5 「更新」ボタンを押下
              <br />
              6 入荷待ち画面に商品が登録されているかご確認ください。
              <br />
              <br />
              ■ FNSKUの確認方法
              <br />
              1. セラーセントラルにログイン
              <br />
              2. 「レポート」 → 「Fulfillment」 → 「在庫」 → 「FBA在庫管理」
              を選択
              <br />
              3. 「CSV形式でのダウンロードをリクエスト」
              <br />
              4. ダウンロードしたCSVからFNSKUが確認可能です。
            </Typography>
          </Box>
        </Box>{" "}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CsvImportDialog;
